import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@cere/cere-design-system';
import type { Member, MemberRole } from '@cef-ai/vault-sdk';
import { addOrgMember, canonicalMemberPubkey, toExpiryRfc3339 } from '@/lib/clients/vault/org-members';
import { toDisplayError } from '@/lib/vault/step-up-error';
import { useVaultSession } from '@/src/state/session';
import '@/src/styles/orgs.css';

interface Props {
  open: boolean;
  /** The vault the section is administering — the caller's OWN vault id on
   *  the My-vault selection (the owner invites into it directly), the
   *  selected org's id otherwise. */
  vaultId: string;
  /** The selected vault's CURRENT roster, as the section last read it. The
   *  re-role guard reads it to recognize a known key BEFORE the write: a
   *  fresh roster that has not landed yet simply matches nothing, and the
   *  server's own 409/upsert semantics stay the backstop. */
  members: Member[];
  /** Fired once the write commits, so the section can re-read the roster. */
  onAdded: () => void;
  onClose: () => void;
}

/**
 * Invite a member into the selected org (or re-role them, when the key is
 * already on the roster — the PUT is an upsert). One dialog, one write:
 * `memberships.put(vaultId, pubkey, role, expiresAt?)`, signed inside the
 * submit click. The write carries the owner's grant-document signature, whose
 * bytes the server rebuilds from the canonical pubkey and the exact expiry
 * string — both shaped by the client layer, never here.
 *
 * The upsert is guarded: submitting a key the roster already carries stops at
 * a blocking confirmation that names the member's CURRENT role (the form's
 * default role would otherwise silently demote an existing admin); only the
 * explicit confirm performs the write.
 */
export function AddMemberDialog({ open, vaultId, members, onAdded, onClose }: Props) {
  const session = useVaultSession();
  const [pubkey, setPubkey] = useState('');
  const [role, setRole] = useState<MemberRole>('member');
  const [expires, setExpires] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The roster row a submit matched, held while the blocking confirmation is
  // up. Null on the form step; cleared by Back, by a close/reopen, and after
  // a committed write — never pre-approved across submissions.
  const [confirming, setConfirming] = useState<Member | null>(null);

  // A closed dialog must not reopen on its confirmation step: the guard is
  // per-submission, not per-mount.
  useEffect(() => { if (!open) setConfirming(null); }, [open]);

  // Expiry is a tri-state, not boolean: empty = no expiry chosen (a valid
  // request without the field), non-empty-but-unparseable = a field error.
  // Collapsing the two made an untouched form unsubmitable.
  const canonical = canonicalMemberPubkey(pubkey);
  const expiry = expires === '' ? undefined : toExpiryRfc3339(expires);
  const keyError = pubkey.trim() !== '' && canonical === null;
  const valid = canonical !== null && (expires === '' || expiry !== undefined);

  async function handleSubmit() {
    if (!valid) return;
    // The known-key stop: `memberships.put` re-roles whoever the key names,
    // so the first submit for a roster member holds at the confirmation
    // step. The confirm button re-enters here WITH `confirming` held and the
    // form hidden, which is what makes the second pass explicit rather than
    // a retry.
    const existing = members.find((m) => canonicalMemberPubkey(m.memberPubkey) === canonical);
    if (existing !== undefined && confirming === null) {
      setConfirming(existing);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // The signing handle is resolved INSIDE this click: resolving it costs a
      // signed `vault.current()`, and when the wallet is cold that is where
      // the step-up ceremony opens — inside the gesture that asked for the
      // write. `memberships` is not on the Vault handle, so the write itself
      // rides the signing SDK root, whose keys the handle has just warmed.
      await session.signingHandle();
      await addOrgMember(session.sdk, vaultId, {
        memberPubkey: canonical,
        role,
        ...(expiry ? { expiresAt: expiry } : {}),
      });
      onAdded();
      onClose();
      // Per-member fields clear for the next invite — a pubkey is single-use
      // (resubmitting it would silently re-role an existing member). The role
      // choice survives: invites usually come in batches of one role.
      setConfirming(null);
      setPubkey('');
      setExpires('');
    } catch (e) {
      // Classified, not raw: a step-up failure must read as the actionable
      // reconnect line. The typed fields survive, so a retry is one click.
      setError(toDisplayError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const expiryPhrase = expiry ? ` and set their access to expire ${expiry}` : '';

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title={confirming !== null ? 'Change an existing member' : 'Add member'}
      onClose={() => !submitting && onClose()}
      isLoading={submitting}
      customActions={
        confirming !== null ? (
          <>
            <Button variant="tertiary" onClick={() => setConfirming(null)} disabled={submitting}>Back</Button>
            <Button variant="primary" disabled={submitting} onClick={() => { void handleSubmit(); }}>
              {submitting ? 'Updating…' : 'Update member'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="tertiary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button variant="primary" disabled={!valid || submitting} onClick={() => { void handleSubmit(); }}>
              {submitting ? 'Adding…' : 'Add member'}
            </Button>
          </>
        )
      }
    >
      {confirming !== null ? (
        <Box className="vault-orgs__wizard-body" data-testid="add-member-confirm">
          <Alert severity="warning">
            {`${canonical} is already on this roster as ${confirming.role}.`}
          </Alert>
          <p className="vault-orgs__note">
            {role === confirming.role
              ? `Submitting re-signs their membership with the same role${expiryPhrase}.`
              : `Submitting changes their role from ${confirming.role} to ${role}${expiryPhrase}.`}
            {' Only continue if that is what you meant to do.'}
          </p>
        </Box>
      ) : (
        <Box className="vault-orgs__wizard-body">
          <TextField
            fullWidth
            label="Member public key"
            value={pubkey}
            onChange={(e) => setPubkey(e.target.value)}
            error={keyError}
            helperText={keyError
              ? 'That is not a wallet public key — expected 0x followed by 64 hex characters.'
              : 'The member’s wallet public key (0x…). Pasted spellings are canonicalised.'}
          />
          <div className="vault-orgs__field-row">
            <span className="vault-orgs__field-label">Role</span>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={role}
              onChange={(_, value) => { if (value !== null) setRole(value as MemberRole); }}
            >
              <ToggleButton value="member">Member</ToggleButton>
              <ToggleButton value="admin">Admin</ToggleButton>
            </ToggleButtonGroup>
          </div>
          <TextField
            fullWidth
            type="date"
            label="Access expires (optional)"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            helperText="Optional — access runs through the end of this day (UTC). Leave empty for no expiry."
          />
          {error && <Alert severity="error" className="vault-orgs__wizard-error">{error}</Alert>}
        </Box>
      )}
    </Dialog>
  );
}
